<script setup lang="ts">
import { computed, onMounted, ref } from 'vue';
import { Mail, Save, Trash2 } from '@lucide/vue';
import { useRouter } from 'vue-router';
import AppShell from '../components/AppShell.vue';
import ConsentTemplatesEditor from '../components/ConsentTemplatesEditor.vue';
import { api } from '../api/client';

const router = useRouter();
const settings = ref<{ uploadLimitMb: number; uploadCeilingMb: number; uploadLimitCustomized: boolean; updatedAt: string | null; updatedBy: string | null }>();
const limit = ref<number>(10);
const loading = ref(true);
const saving = ref(false);
const error = ref('');
const notice = ref('');
const presets = [10, 20, 50, 100, 500, 1024, 2048];
// Почта для отправки ответов
const smtp = ref<any>();
const smtpForm = ref({ host: '', port: 465, secure: true, user: '', password: '', from: '' });
const smtpSaving = ref(false);
const smtpError = ref('');
const smtpNotice = ref('');
const testTo = ref('');
const testState = ref('');
function fillSmtpForm() { if (smtp.value) smtpForm.value = { host: smtp.value.host, port: smtp.value.port, secure: smtp.value.secure, user: smtp.value.user, password: '', from: smtp.value.from }; }
function onSecurityChange() { smtpForm.value.port = smtpForm.value.secure ? 465 : 587; }
async function saveSmtp() {
  smtpError.value = ''; smtpNotice.value = ''; smtpSaving.value = true;
  try { smtp.value = await api.saveSmtpSettings({ ...smtpForm.value, port: Number(smtpForm.value.port) }); fillSmtpForm(); smtpNotice.value = 'Настройки почты сохранены. Отправьте тестовое письмо, чтобы проверить.'; }
  catch (e: any) { if (e.status === 401) return router.push('/login'); smtpError.value = e.message; }
  finally { smtpSaving.value = false; }
}
async function clearSmtp() {
  if (!confirm('Удалить настройки почты из админки? Будут использоваться значения из .env, если они заданы.')) return;
  smtpError.value = ''; smtpNotice.value = '';
  try { smtp.value = await api.clearSmtpSettings(); fillSmtpForm(); smtpNotice.value = 'Настройки почты из админки удалены.'; } catch (e: any) { smtpError.value = e.message; }
}
async function sendTest() {
  testState.value = 'Отправляем…';
  try { await api.sendTestEmail(testTo.value.trim()); testState.value = `Тестовое письмо отправлено на ${testTo.value.trim()}. Проверьте папку «Входящие» и «Спам».`; }
  catch (e: any) { testState.value = e.message; }
}
const formatMb = (mb: number) => (mb >= 1024 ? `${+(mb / 1024).toFixed(1)} ГБ` : `${mb} МБ`);
const availablePresets = computed(() => presets.filter((mb) => mb <= (settings.value?.uploadCeilingMb ?? 0)));

async function load() {
  try {
    const me = await api.me();
    if (me.role !== 'admin') return router.push('/');
    settings.value = await api.settings();
    limit.value = settings.value.uploadLimitMb;
    smtp.value = await api.smtpSettings();
    fillSmtpForm();
    testTo.value = me.email;
  } catch (e: any) {
    if (e.status === 401) return router.push('/login');
    error.value = e.message;
  } finally { loading.value = false; }
}

async function save() {
  error.value = ''; notice.value = '';
  saving.value = true;
  try {
    settings.value = await api.saveSettings({ uploadLimitMb: Number(limit.value) });
    notice.value = `Сохранено. Во всех формах теперь можно загружать файлы до ${formatMb(settings.value.uploadLimitMb)}.`;
  } catch (e: any) {
    if (e.status === 401) return router.push('/login');
    error.value = e.message;
  } finally { saving.value = false; }
}

onMounted(load);
</script>

<template>
  <AppShell>
    <div class="page-heading"><div><h1>Настройки</h1><p>Параметры сервиса для всех форм · доступно только администраторам</p></div></div>
    <div v-if="loading" class="users-panel skeleton" style="min-height:160px"></div>
    <p v-else-if="error && !settings" class="alert error">{{ error }}</p>
    <form v-else-if="settings" class="users-panel" @submit.prevent="save">
      <header><h2>Загрузка файлов</h2></header>
      <label class="input-label">Максимальный размер одного файла, МБ
        <input v-model.number="limit" class="control settings-number" type="number" min="1" :max="settings.uploadCeilingMb" required/>
      </label>
      <div class="settings-presets">
        <button v-for="mb in availablePresets" :key="mb" type="button" class="btn btn-quiet" :class="{ active: limit === mb }" @click="limit = mb">{{ formatMb(mb) }}</button>
      </div>
      <p class="users-hint">
        Действует во всех формах сразу, перезапуск не нужен. В настройке поля можно задать лимит меньше этого.
        Потолок сервера — {{ formatMb(settings.uploadCeilingMb) }} (задан в .env и Nginx; больше указать нельзя).
      </p>
      <p class="users-hint">Учитывайте место на диске: каждая анкета хранит все приложенные файлы.</p>
      <p v-if="settings.updatedAt" class="users-hint">Последнее изменение: {{ new Date(settings.updatedAt).toLocaleString('ru-RU') }}<template v-if="settings.updatedBy"> · {{ settings.updatedBy }}</template></p>
      <p v-if="error" class="alert error">{{ error }}</p>
      <p v-if="notice" class="alert success">{{ notice }}</p>
      <div><button class="btn btn-primary" :disabled="saving"><Save :size="16"/>Сохранить</button></div>
    </form>

    <form v-if="smtp" class="users-panel" autocomplete="off" @submit.prevent="saveSmtp">
      <header><h2>Почта для отправки ответов</h2></header>
      <p class="users-hint">
        <template v-if="smtp.source === 'admin'">Используются настройки из этого раздела.</template>
        <template v-else-if="smtp.source === 'env'">Сейчас используются настройки из файла .env на сервере. Сохранённые здесь настройки будут важнее.</template>
        <template v-else>Почта не настроена: письма с ответами ждут в очереди и уйдут после настройки.</template>
        Данные выдаёт администратор почты (для Яндекс 360: smtp.yandex.ru, порт 465, пароль приложения).
      </p>
      <div class="users-form-grid">
        <label class="input-label">SMTP-сервер<input v-model="smtpForm.host" class="control" placeholder="smtp.yandex.ru" required/></label>
        <label class="input-label">Шифрование<select v-model="smtpForm.secure" class="control" @change="onSecurityChange"><option :value="true">SSL/TLS (обычно порт 465)</option><option :value="false">STARTTLS или без шифрования (обычно 587)</option></select></label>
        <label class="input-label">Порт<input v-model.number="smtpForm.port" class="control" type="number" min="1" max="65535" required/></label>
        <label class="input-label">Логин (адрес ящика)<input v-model="smtpForm.user" class="control" placeholder="forms@bgpu.ru" autocomplete="off"/></label>
        <label class="input-label">Пароль<input v-model="smtpForm.password" class="control" type="password" autocomplete="new-password" :placeholder="smtp.hasPassword ? 'Сохранён — оставьте пустым, чтобы не менять' : 'Пароль ящика или приложения'"/></label>
        <label class="input-label">Отправитель<input v-model="smtpForm.from" class="control" placeholder="Формы БГПУ <forms@bgpu.ru>" required/></label>
      </div>
      <p v-if="smtp.passwordUnreadable" class="alert error">Сохранённый пароль не удаётся прочитать (сменился JWT_SECRET на сервере). Введите пароль заново.</p>
      <p class="users-hint">Пароль хранится в базе в зашифрованном виде и не показывается после сохранения.</p>
      <p v-if="smtp.updatedAt" class="users-hint">Последнее изменение: {{ new Date(smtp.updatedAt).toLocaleString('ru-RU') }}<template v-if="smtp.updatedBy"> · {{ smtp.updatedBy }}</template></p>
      <p v-if="smtpError" class="alert error">{{ smtpError }}</p>
      <p v-if="smtpNotice" class="alert success">{{ smtpNotice }}</p>
      <div class="smtp-actions"><button class="btn btn-primary" :disabled="smtpSaving"><Save :size="16"/>Сохранить почту</button><button v-if="smtp.source === 'admin'" type="button" class="btn btn-quiet" @click="clearSmtp"><Trash2 :size="16"/>Удалить из админки</button></div>
      <div v-if="smtp.source !== 'none'" class="smtp-test">
        <label class="input-label">Проверка: отправить тестовое письмо на<input v-model="testTo" class="control" type="email"/></label>
        <button type="button" class="btn btn-quiet" :disabled="!testTo" @click="sendTest"><Mail :size="16"/>Отправить тестовое письмо</button>
        <p v-if="testState" class="users-hint">{{ testState }}</p>
      </div>
    </form>

    <ConsentTemplatesEditor v-if="settings"/>
  </AppShell>
</template>
